import styled, { Interpolation } from '@emotion/styled';
import { NavigationMenu } from '@base-ui/react/navigation-menu';
import React, { type ReactElement } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import useMode from '../../hooks/useMode';
import Icon, { type IconType } from '../Icon/Icon';
import { Theme } from '@/theme';
import { css } from '@emotion/core';

const ItemIcon = styled(Icon)`
  flex-shrink: 0;
`;

const Content = styled.span`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space[2]}px;
  white-space: nowrap;
  user-select: none;
`;

const containerStyles: Interpolation<{
  readonly theme: Theme;
  readonly nested?: boolean;
}> = ({ theme, nested }) => `
  border-left: 3px solid transparent;

  ${
    nested
      ? `
          padding: ${theme.space[1]}px ${theme.space[3]}px;
          padding-left: calc(16px + ${theme.space[2]}px + ${theme.space[3]}px);
          color: ${theme.colors.onNavbar};
          font-size: ${theme.fontSizes[2]}px;
          font-weight: ${theme.fontWeights.body};
        `
      : `
          padding: ${theme.space[2]}px ${theme.space[3]}px;
          color: ${theme.colors.base.gray[500]};
          font-size: ${theme.fontSizes[1]}px;
          font-weight: ${theme.fontWeights.heading};
          text-transform: uppercase;
          letter-spacing: 0.06em;
        `
  }`;

const activeStyles: Interpolation<{ readonly theme: Theme }> = ({ theme }) => `
  &[data-active] {
    color: ${theme.colors.onNavbarHover};
    background-color: ${theme.colors.base.gray[100]};
    border-left-color: ${theme.colors.navbarHover};
  }
`;

const targetedStyles: Interpolation<{ readonly theme: Theme }> = ({
  theme,
}) => `
  &:hover,
  &:focus-visible {
    outline: none;
    color: ${theme.colors.onNavbarHover};
    background-color: ${theme.colors.base.gray[100]};
  }
`;

const LinkWrapper = styled(NavLink)<{ nested?: boolean }>`
  display: block;
  text-decoration: none;
  transition: all 0.2s ease;
  line-height: 1.4;

  ${containerStyles}
  ${activeStyles}
  ${targetedStyles}
`;

const LabelWrapper = styled.div`
  ${containerStyles}
`;

type Props = {
  readonly label: string;
  readonly id?: string;
  readonly href?: string;
  readonly exact?: boolean;
  readonly nested?: boolean;
  readonly icon?: IconType;
};

const SidebarItem = ({
  label,
  id,
  href,
  exact,
  nested,
  icon,
}: Props): ReactElement => {
  const mode = useMode();
  const location = useLocation();

  const fullPath = `/${mode}${href ?? ''}`;
  const active = exact
    ? location.pathname === fullPath
    : location.pathname.startsWith(fullPath) && fullPath !== `/${mode}`;

  const content = (
    <Content>
      {icon && !nested ? (
        <ItemIcon icon={icon} size={16} aria-hidden={true} />
      ) : null}
      {label}
    </Content>
  );

  return href !== undefined ? (
    <NavigationMenu.Item>
      <NavigationMenu.Link
        active={active}
        render={
          <LinkWrapper id={id} to={fullPath} nested={nested}>
            {content}
          </LinkWrapper>
        }
      />
    </NavigationMenu.Item>
  ) : (
    <LabelWrapper id={id}>{content}</LabelWrapper>
  );
};

export default SidebarItem;
